import { useEffect, useMemo, useState } from 'react';
import { gql, useApolloClient, useReactiveVar } from '@apollo/client';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { useMenuPeriodsData } from 'app/hooks/accessors';

const PERIODS_LIMIT = 6;

interface IPeriod {
    start?: string;
}

const DISH_AVAILABILITY_QUERY = gql`
    query BASKET_TOP_DISH_AVAILABILITY($id: ID!, $period: String!) {
        menuDish(id: $id, period: $period) {
            id
        }
    }
`;

/**
 * @description ищет первый период начиная с выбранного, в котором блюдо есть в меню
 * @returns { firstAvailablePeriod: string | null, isLoading: boolean }
 */
export const useFirstDishAvailablePeriod = (dishId: string) => {
    const client = useApolloClient();
    const period = useReactiveVar(selectedPeriodVar);
    const { periods } = useMenuPeriodsData();

    const [firstAvailablePeriod, setFirstAvailablePeriod] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const periodsToCheck = useMemo(() => ((periods || []) as IPeriod[])
        .map((item) => item?.start)
        .filter((start): start is string => typeof start === 'string' && start >= period)
        .sort()
        .slice(0, PERIODS_LIMIT),
    [period, periods],
    );

    useEffect(() => {
        let isActual = true;

        if (!dishId || !periodsToCheck.length) {
            setFirstAvailablePeriod(null);
            setIsLoading(false);
            return () => { isActual = false; };
        }

        setIsLoading(true);

        const availabilityPromises = periodsToCheck.map((itemPeriod: string) => client.query({
            query: DISH_AVAILABILITY_QUERY,
            variables: { id: dishId, period: itemPeriod },
            fetchPolicy: 'cache-first',
            context: {
                message: 'basket:useFirstDishAvailablePeriod',
            },
        }).then(({ data }) => Boolean(data?.menuDish)).catch(() => false));

        Promise.all(availabilityPromises).then((availabilityList) => {
            if (!isActual) return;

            const firstAvailableIndex = availabilityList.findIndex(Boolean);
            setFirstAvailablePeriod(
                firstAvailableIndex === -1 ? null : periodsToCheck[firstAvailableIndex],
            );
            setIsLoading(false);
        });

        return () => { isActual = false; };
    }, [client, dishId, periodsToCheck]);

    return { firstAvailablePeriod, isLoading };
};
